import { FloatingFocusManager, FloatingPortal, useId } from '@floating-ui/react';
import { i18n } from '@lingui/core';
import { combobox as ccCombobox } from '@warp-ds/css/component-classes';
import { Affix, TextField } from '@warp-ds/react';
import cx from 'classnames';
import type React from 'react';
import { forwardRef } from 'react';
import type { ValueOption } from '../../types/widget.js';
import { useCombobox } from '../hooks/useCombobox.js';

interface ItemProps extends React.HTMLProps<HTMLDivElement> {
  children: React.ReactNode;
  active: boolean;
}

export const Item = forwardRef<HTMLDivElement, ItemProps>(({ children, active, ...rest }, ref) => {
  const id = useId();
  return (
    <div
      ref={ref}
      // biome-ignore lint/a11y/useSemanticElements: afraid of styling being broken
      role="option"
      id={id}
      tabIndex={0}
      aria-selected={active}
      {...rest}
      className={cx(ccCombobox.option, active ? ccCombobox.optionSelected : ccCombobox.optionUnselected)}
    >
      {children}
    </div>
  );
});

interface ComboboxProps {
  name?: string;
  label: string;
  options: ValueOption[];
  onSelect: (option?: ValueOption) => void;
  invalid?: boolean;
  helpText?: string;
  value?: number;
  className?: string;
  onBlur?: (inputValue: string) => void;
}

export const Combobox = ({
  label,
  options,
  onSelect,
  invalid,
  helpText,
  value,
  className,
  onBlur,
  ...rest
}: ComboboxProps) => {
  const {
    searchText,
    setSearchText,
    setActive,
    setOpen,
    open,
    filteredOptions,
    highlightFuzzyResult,
    x,
    y,
    strategy,
    refs,
    context,
    getReferenceProps,
    getFloatingProps,
    getItemProps,
    activeIndex,
    listRef,
    handleSelect,
    handleChange,
    clearText,
  } = useCombobox({
    options,
    onSelect: (option) => {
      setSearchText(option?.label || '');
      onSelect(option);
      setOpen(false);
    },
    initialValue: options.find((option) => option.id === value)?.label ?? '',
  });

  const conditionallyOpen = () => {
    if (value || open) return;
    refs.domReference.current?.focus();
    setOpen(true);
  };

  const selectAndClose = (option: ValueOption) => {
    handleSelect(option);
    setActive(null);
    setOpen(false);
  };

  const clearTextAriaLabel = i18n.t({
    id: 'combobox.clearText.ariaLabel',
    message: 'Clear text',
    comment: 'Aria-label for the clear text button in the combobox',
  });

  return (
    <>
      <TextField
        {...getReferenceProps({
          value: searchText,
          onChange: handleChange,
          onClick: () => conditionallyOpen(),
          onBlur: () => {
            setOpen(false); // Close dropdown on blur
            onBlur?.(searchText); // Pass current input value to onBlur
          },
          onKeyDown: (event) => {
            if (event.key === 'ArrowDown' && !open) {
              conditionallyOpen();
            }
            if (event.key === 'Enter' && activeIndex != null && filteredOptions[activeIndex]) {
              event.preventDefault();
              event.stopPropagation();
              const selectedOption = filteredOptions[activeIndex].obj;
              selectAndClose(selectedOption);
            }
          },
        })}
        ref={refs.setReference}
        label={label}
        invalid={invalid}
        helpText={helpText}
        className={className}
        autoComplete="off"
        {...rest}
      >
        {searchText && (
          <Affix
            suffix
            clear
            aria-label={clearTextAriaLabel}
            onClick={() => {
              clearText();
              onSelect(undefined);
              refs.domReference.current?.focus();
            }}
          />
        )}
      </TextField>

      {open && filteredOptions.length > 0 && (
        <FloatingPortal>
          <FloatingFocusManager context={context} initialFocus={-1} visuallyHiddenDismiss>
            <div
              {...getFloatingProps({
                ref: refs.setFloating,
                style: {
                  position: strategy,
                  top: y ?? 0,
                  left: x ?? 0,
                  overflowY: 'auto',
                  border: '1px solid lightgrey',
                  borderRadius: '5px',
                  background: 'white',
                  zIndex: 30,
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: '0px 2px 8px 0px rgba(36, 50, 64, 0.08)',
                },
              })}
            >
              {filteredOptions.map((result, index: number) => {
                const option = result.obj;
                let content: string | (string | React.ReactElement)[] | null = null;
                if (searchText.trim().length > 0 && result) {
                  content = highlightFuzzyResult(result);
                } else {
                  content = option.label;
                }
                return (
                  <Item
                    key={option.id}
                    {...getItemProps({
                      ref(node: HTMLDivElement) {
                        listRef.current[index] = node;
                      },
                      onMouseDown: (event) => {
                        // Use onMouseDown to prevent onBlur timing issue
                        event.preventDefault(); // Prevent input from losing focus
                        selectAndClose(option);
                      },
                      role: 'option',
                      tabIndex: activeIndex === index ? 0 : -1,
                    })}
                    active={activeIndex === index}
                  >
                    {content}
                  </Item>
                );
              })}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};
